import { Spinner } from "@procore/core-react";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { Node, Tree } from "components/objectTree/types";
import type { ModelTreeNodeData } from "features/modelTree/types";

import { ITEM_HEIGHT_IOS } from "../constants";
import { MobileObjectTreeRow } from "./MobileObjectTreeRow";
import { SwipeableRow } from "./SwipeableRow";

interface MobileObjectTreeLevelProps {
    tree: Tree<ModelTreeNodeData>;
    drillPath: string;
    onDrillIn: (path: string) => void;
    onLeafTap: (node: Node<ModelTreeNodeData>) => void;
    onLoadMore: () => void;
}

export function MobileObjectTreeLevel({
    tree,
    drillPath,
    onDrillIn,
    onLeafTap,
    onLoadMore,
}: MobileObjectTreeLevelProps) {
    const { t } = useTranslation();
    const node = tree[drillPath];
    const activeRowCloseRef = useRef<(() => void) | null>(null);

    const handleRowOpen = useCallback((closeFn: () => void) => {
        if (activeRowCloseRef.current !== closeFn) {
            activeRowCloseRef.current?.();
        }
        activeRowCloseRef.current = closeFn;
    }, []);

    const handleDrillIn = useCallback(
        (path: string) => {
            activeRowCloseRef.current?.();
            activeRowCloseRef.current = null;
            onDrillIn(path);
        },
        [onDrillIn],
    );

    if (!node || node.loading || !node.children) {
        return (
            <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                <Spinner />
            </div>
        );
    }

    const children = node.children.map((path) => tree[path]).filter((n): n is Node<ModelTreeNodeData> => n != null);

    return (
        <div style={{ height: "100%", overflowY: "auto" }}>
            {children.map((child) => (
                <SwipeableRow key={child.path} node={child} onOpen={handleRowOpen}>
                    <MobileObjectTreeRow node={child} tree={tree} onDrillInto={handleDrillIn} onLeafTap={onLeafTap} />
                </SwipeableRow>
            ))}
            {node.hasMoreChildren && (
                <button
                    type="button"
                    onClick={onLoadMore}
                    style={{
                        display: "block",
                        width: "100%",
                        minHeight: ITEM_HEIGHT_IOS,
                        padding: "0 16px",
                        background: "none",
                        border: "none",
                        borderBottom: "1px solid rgba(172, 181, 185, 0.32)",
                        cursor: "pointer",
                        textAlign: "left",
                        fontSize: 16,
                        fontWeight: 500,
                        color: "#007aff",
                        fontFamily: "inherit",
                    }}
                >
                    {t("loadMoreObjects")}
                </button>
            )}
        </div>
    );
}
